import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Menu, { MenuItem, SubMenu } from 'rc-menu/es';
import Autosuggest from 'react-autosuggest';
import {
  GoKebabVertical, GoSearch, MdGroup, FaBolt, FaBookmark,
} from 'react-icons/all';
import { FaRegBookmark } from 'react-icons/fa';
import DownloadModal from '../download_modal/DownloadModal';
import api from '../../services/api';
import CaseDigestPreview from '../case_digest/CaseDigestPreview';
import Snackbar from '../../services/snackbar';
import DynamicSearchParams from './DynamicSearchParams';
import VideoModal from '../video/VideoModal';
import DynamicCiteListNotAvailableModal from '../search_modal/DynamicCiteListNotAvailableModal';
import PaywallModal from './modals/PaywallModal';
import ConfirmCiteListDeletionModalOpen from './modals/ConfirmCiteListDeletionModalOpen';
import SelectedCasesSheet from './SelectedCasesSheet';
import NameEditModal from './modals/NameEditModal';
import PdfModal from './modals/PdfModal';
import DeleteListModal from './modals/DeleteListModal';
import ShareSection from './ShareSection';
import SharedTo from './modals/SharedTo';
import SearchLoadingModal from '../search/SearchLoadingModal';

export default function CiteLists({
  tags, jurisdictions, bookmarks, currentUser, citeListId, numberOfDownloads, teamMembers, isIndividual, plan_name, account_name, hasFullAccess, hasDynamicAccess,
}) {
  const [citeListType, setCiteListType] = useState('my');
  const [selectedCiteList, setSelectedCiteList] = useState(null);
  const [selectedCiteListCases, setSelectedCiteListCases] = useState([]);
  const [nameEditModalId, setNameEditModalId] = useState(-1);
  const [pdfModalId, setPdfModalId] = useState(-1);
  const [deleteListModalId, setDeleteListModalId] = useState(-1);
  const [searchValue, setSearchValue] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const [shareSectionOpen, setShareSectionOpen] = useState(false);
  const [confirmCiteListDeletionModalOpen, setConfirmCiteListDeletionModalOpen] = useState(false);
  const [downloadLimit, setDownloadLimit] = useState(numberOfDownloads);
  const [favorites, setFavorites] = useState(bookmarks);
  const [page, setPage] = useState(1);
  const [citeListCases, setCiteListCases] = useState(null);
  const [citeLists, setCiteLists] = useState([]);
  const [sharedCiteLists, setSharedCiteLists] = useState([]);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [videoModalResult, setVideoModalResult] = useState(null);
  const [citeListNotAvailableModalVisible, setCiteListNotAvailableModalVisible] = useState(false);
  const [downloadAction, setDownloadAction] = useState(false);
  const [loader, setLoader] = useState(false);
  const [sortBy, setSortBy] = useState('des_decision_date');
  const [clickedId, setClickedId] = useState(null);
  const hasDownloadLimit = plan_name === 'Trial' || plan_name === 'Individual Monthly';

  const searchInputProps = {
    placeholder: 'Search By Cite List Name',
    value: searchValue,
    onChange: (e, { newValue }) => setSearchValue(newValue),
  };

  const cases_count = selectedCiteList?.search_query ? selectedCiteList?.search_count : selectedCiteList?.cases_count;

  const fetchCiteListCases = (pageNumber) => {
    if (!selectedCiteList || !selectedCiteList.id) return;

    setLoader(true);
    setCiteListCases(null);
    setSelectedCiteListCases([]);
    setPage(pageNumber);

    api.cite_list_cases.get(selectedCiteList.id, { page: pageNumber }, sortBy).then((res) => {
      if (selectedCiteList.id === res.list_id) {
        setCiteListCases(res.message);
        localStorage.setItem('CASE_DIGEST_SEARCH_RESULTS', JSON.stringify({ slugs: res.slugs, search_mode: 'cite_list', citeListId: selectedCiteList.id }));
      }
      setLoader(false);
    });
  };

  const fetchCiteLists = async (listId) => {
    setLoader(true);
    const res = await api.cite_lists.get({ withShared: true });
    const lists = [];
    const sharedLists = [];

    res.message.forEach((list) => {
      if (list.user.id === currentUser.id) lists.push(list);
      else sharedLists.push(list);
    });

    setCiteLists(lists);
    setSharedCiteLists(sharedLists);

    if (lists.length !== 0 || sharedLists.length !== 0) {
      // check if current_user has access to the dynamic features
      // set first regular cite list as selected if current_user does not have access
      const firstRegularCiteList = lists.find((l) => l.search_query === null || l.search_query === '') || null;
      const firstDynamicCiteList = lists.find((l) => l.search_query) || null;
      const indexOfFirstRegular = lists.indexOf(firstRegularCiteList) === -1 ? Infinity : lists.indexOf(firstRegularCiteList);
      const indexOfFirstDynamic = lists.indexOf(firstDynamicCiteList) === -1 ? Infinity : lists.indexOf(firstDynamicCiteList);
      const firstToChoose = indexOfFirstRegular < indexOfFirstDynamic ? firstRegularCiteList : firstDynamicCiteList;

      const citeListToAutoselect = hasDynamicAccess ? (firstToChoose || null) : (firstRegularCiteList || null);
      const citeListToSelect = selectedCiteList
        ? res.message.find((list) => list.id === selectedCiteList.id)
        : res.message.find((list) => list.id === listId);
      // fetch cases for 1st page only if list was changed
      const checkedCiteList = citeListToSelect || citeListToAutoselect;
      if (lists.find((list) => list.id === checkedCiteList?.id)) setCiteListType('my');
      if (sharedLists.find((sharedList) => sharedList.id === checkedCiteList?.id)) setCiteListType('shared');
      setSelectedCiteList(checkedCiteList?.id ? { ...checkedCiteList, prevId: selectedCiteList?.id } : null);
    }
    setLoader(false);
  };

  const escFunction = (event) => {
    if (event.keyCode === 27) {
      setSelectedCiteListCases([]);
    }
  };

  useEffect(() => {
    fetchCiteLists(citeListId);
    document.addEventListener('keydown', escFunction);
    localStorage.setItem('CASE_DIGEST_SEARCH_RESULTS', JSON.stringify({ search_mode: 'cite_list' }));
    const urlSearchParams = new URLSearchParams(window.location.search);
    setClickedId(urlSearchParams.get('clickedId'));

    return () => document.removeEventListener('keydown', escFunction);
  }, []);

  useEffect(() => {
    if (selectedCiteList?.id === selectedCiteList?.prevId) return;

    fetchCiteListCases(1);
  }, [selectedCiteList]);

  const updateFavorites = () => {
    api.favorites.get().then((result) => setFavorites(result.favorites));
  };

  const toggleCaseBookmark = (item) => {
    if (favorites.case_digests !== undefined && favorites.case_digests.some((favorite) => favorite.id === item.id)) {
      api.case_digests.unfavorite(item.id).then(() => updateFavorites());
      return;
    }
    api.case_digests.favorite(item.id).then(() => updateFavorites());
  };

  const toggleCiteListFavorite = (citeList) => {
    api.cite_lists.favorite(citeList.id)
      .then((res) => {
        updateFavorites();
        if (res.favorite) Snackbar.show(`Cite list named ${citeList.name} was added to the Bookmarks.`);
        else Snackbar.show(`Cite list named ${citeList.name} was removed from the Bookmarks.`);
      });
  };

  const clearSearchSuggestions = () => {
    if (searchValue !== '') return;
    setSearchSuggestions([]);
  };

  const onSort = async (value) => {
    setLoader(true);
    setSortBy(value);
    setSelectedCiteListCases([]);
    await api.cite_list_cases.get(selectedCiteList.id, { page }, value).then((res) => {
      setCiteListCases(res.message);
    });
    setLoader(false);
  };

  const deleteSelectedCiteListCasesFromCiteList = () => {
    api.cite_list_cases.mass_unassign({
      case_digest_ids: selectedCiteListCases.map((citeListCase) => citeListCase.case_digest_id),
      cite_list_id: selectedCiteList.id,
    })
      .then(() => fetchCiteLists())
      .then(() => {
        api.cite_list_cases.get(selectedCiteList.id, { page }).then((res) => {
          setSelectedCiteListCases([]);
          setCiteListCases(res.message);
        });
      });
  };

  const downloadCiteList = (citeList) => {
    if (isIndividual) {
      setDownloadAction(false);
      setPaywallModalOpen(true);
      return;
    }

    if (!hasFullAccess) {
      setDownloadAction(true);
      setPaywallModalOpen(true);
      return;
    }

    const casesCount = citeList.search_query ? citeList.search_count : citeList.cases_count;

    if (hasDownloadLimit) setDownloadModalOpen(true);
    if (hasDownloadLimit && downloadLimit < casesCount) return;

    setDownloadLimit(downloadLimit - casesCount);
    setDownloadAction(true);

    api.cite_lists.exportCiteList(citeList.id, { expand_digest: 'on' });
    setPdfModalId(citeList.id);
  };

  const downloadCiteListPdf = (citeList) => {
    if (isIndividual) {
      setDownloadAction(false);
      setPaywallModalOpen(true);
      return;
    }

    if (!hasFullAccess) {
      setDownloadAction(true);
      setPaywallModalOpen(true);
      return;
    }

    if (hasDownloadLimit) setDownloadModalOpen(true);
    if (hasDownloadLimit && downloadLimit === 0) return;

    setDownloadLimit(downloadLimit - 1);
    setDownloadAction(true);

    api.cite_lists.exportCiteListPdf(citeList.id, { expand_digest: 'on' });
    setPdfModalId(citeList.id);
  };

  const convertToStatic = async (citeList) => {
    setLoader(true);
    const { status } = await api.cite_lists.dynamic_to_static(citeList.id);

    if (status === 'OK') {
      await fetchCiteLists(citeListId);
    } else {
      Snackbar.show('Something went wrong.');
    }
    setLoader(false);
  };

  const downloadSelectedCases = () => {
    if (!hasFullAccess && !isIndividual) {
      setDownloadAction(true);
      setPaywallModalOpen(true);
      return;
    }

    if (hasDownloadLimit) setDownloadModalOpen(true);
    if (hasDownloadLimit && selectedCiteListCases.length > downloadLimit) return;

    setDownloadLimit(downloadLimit - selectedCiteListCases.length);
    setDownloadAction(true);

    setDownloadAction(true);
    if (selectedCiteListCases.length > 5) {
      api.cite_lists.exportCiteList(selectedCiteList.id, {
        case_digest_ids: selectedCiteListCases.map((citeListCase) => citeListCase.case_digest.id),
        expand_digest: 'on',
      });
      setDownloadModalOpen(false);
      setPdfModalId(selectedCiteList.id);
    } else {
      setLoader(true);
      selectedCiteListCases.forEach(async (citeListCase) => {
        await api.case_digests
          .downloadPDF(citeListCase.case_digest.id).catch(() => alert('Download failed'));
        setDownloadModalOpen(false);
        setLoader(false);
      });
    }
  };

  const deleteCiteList = (citeList) => {
    api.cite_lists.delete(citeList.id).then(() => {
      if (selectedCiteList?.id === citeList.id) {
        setSelectedCiteList(null);
        setCiteListCases(null);
      }

      fetchCiteLists();
    });
  };

  const selectAll = () => {
    if (!citeListCases) return;
    setSelectedCiteListCases([...citeListCases]);
  };

  const fetchSelectedCiteList = (citeList) => {
    setSortBy('des_decision_date');
    if (!citeList) {
      window.history.replaceState(null, null, '/cite_lists');
      return;
    }

    if (citeList.search_query && !hasDynamicAccess) {
      setCiteListNotAvailableModalVisible(true);
      return;
    }

    if (selectedCiteList && citeList.id === selectedCiteList.id) return;

    setSelectedCiteList(citeList);
    setSelectedCiteListCases([]);
    setPage(1);
    if (citeLists.find((cl) => cl.id === citeList.id)) setCiteListType('my');
    if (sharedCiteLists.find((cl) => cl.id === citeList.id)) setCiteListType('shared');

    window.history.replaceState(null, null, `/cite_lists/${citeList.id}`);
  };

  const getSharedWithList = () => {
    if (!selectedCiteList) return [];
    return [{ cite_list: selectedCiteList, user: selectedCiteList.user, sharer: null }, ...selectedCiteList.cite_list_shares]
      .filter((s) => s.user.id !== currentUser.id);
  };

  const selectCase = (citeListCase) => {
    const caseIndex = selectedCiteListCases.indexOf(citeListCase);
    const casesToSelect = [...selectedCiteListCases];
    if (caseIndex === -1) {
      casesToSelect.push(citeListCase);
    } else {
      casesToSelect.splice(caseIndex, 1);
    }
    setSelectedCiteListCases(casesToSelect);
  };

  const openShareDialog = () => {
    if (!hasFullAccess) {
      setDownloadAction(false);
      setPaywallModalOpen(true);
      return;
    }

    setShareSectionOpen(true);
  };

  return (
    <div className="react-cite-lists">
      <div className="left">
        <div className="name">
          My Cite Lists
        </div>
        <div className="search">
          <Autosuggest
            suggestions={searchSuggestions}
            shouldRenderSuggestions={() => true}
            onSuggestionsFetchRequested={({ value }) => {
              const searchArray = [...citeLists, ...sharedCiteLists];
              const inputValue = value.trim().toLowerCase();
              const suggestions = inputValue.length === 0 ? [] : searchArray.filter((item) => item.name.toLowerCase().includes(inputValue.toLowerCase()));

              setSearchSuggestions(suggestions);
            }}
            onSuggestionsClearRequested={clearSearchSuggestions}
            getSuggestionValue={(item) => item.name}
            renderSuggestion={(item) => <div className="suggestion">{ item.name }</div>}
            inputProps={searchInputProps}
            onSuggestionSelected={(e, { suggestion }) => setSelectedCiteList(suggestion)}
          />
          { searchValue.length > 0 && (
          <span
            className="clear-tags"
            onClick={() => {
              setSearchValue('');
              setSearchSuggestions([]);
            }}
          >
            ✕
          </span>
          )}
          <div className="search-icon">
            <GoSearch />
          </div>
        </div>
        { Boolean(hasFullAccess) && (
        <div className="cite-list-types">
          <div className={`tag ${citeListType === 'my' && 'active'}`} onClick={() => setCiteListType('my')}>
            My Cite Lists
          </div>
          <div className={`tag ${citeListType === 'shared' && 'active'}`} onClick={() => setCiteListType('shared')}>
            Shared with me
          </div>
        </div>
        )}
        <div className="navigator">
          { (citeListType === 'shared' ? sharedCiteLists : citeLists).map((citeList) => (searchSuggestions.length === 0
              || searchSuggestions.some((suggestion) => citeList.id === suggestion.id)) && (
                <>
                  <div
                    className={`cite-list-summary${
                      (selectedCiteList && selectedCiteList.id === citeList.id) ? ' selected' : ''}`}
                    key={citeList.id}
                    onClick={() => fetchSelectedCiteList(citeList)}
                  >
                    <div className="cite-container">
                      <div className="cite-list-data">
                        <div className="cite-list-name">
                          { citeList.name }
                        </div>
                        { citeList.user.id !== currentUser.id && (
                          <div className="cite-list-shared-by">
                            Shared By:
                            {' '}
                            {citeList.cite_list_shares.find((s) => s.user.id === currentUser.id)?.sharer.name}
                          </div>
                        )}
                      </div>
                      { citeList.cite_list_shares.length > 0 && (
                        <div className="header-shared">
                          <MdGroup size={20} style={{ marginRight: '0.8rem' }} />
                        </div>
                      )}
                      { citeList.search_query && <FaBolt style={{ marginRight: '0.8rem' }} /> }
                      <div>
                        { citeList.search_query ? citeList.search_count : citeList.cases_count }
                      </div>
                      <div className="buttons">
                        <div className="options">
                          <Menu mode="horizontal" className="options-menu" triggerSubMenuAction="click">
                            <SubMenu
                              title={<GoKebabVertical />}
                              onTitleClick={(e) => e.domEvent.stopPropagation()}
                              className="settings"
                              popupClassName="settings-submenu"
                            >
                              <MenuItem
                                className="settings-item-new"
                                onClick={(e) => {
                                  e.domEvent.stopPropagation();
                                  openShareDialog(citeList);
                                }}
                              >
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M14.0009 10.6666C13.5085 10.6699 13.023 10.7821 12.5792 10.9954C12.1354 11.2086 11.7444 11.5176 11.4342 11.8999L7.18419 9.9416C7.38401 9.32972 7.38401 8.67014 7.18419 8.05827L11.4342 6.09993C11.9355 6.70491 12.6345 7.11333 13.4077 7.25313C14.1808 7.39293 14.9785 7.25512 15.66 6.86402C16.3414 6.47292 16.8628 5.85366 17.1321 5.11555C17.4015 4.37744 17.4014 3.56792 17.1319 2.82986C16.8625 2.09179 16.341 1.47264 15.6595 1.08166C14.9779 0.690677 14.1802 0.55301 13.4071 0.692952C12.6339 0.832894 11.935 1.24145 11.4338 1.84651C10.9326 2.45157 10.6612 3.21425 10.6675 3.99993C10.67 4.19846 10.6895 4.39641 10.7259 4.5916L6.32586 6.6166C5.85676 6.15792 5.26273 5.84786 4.6182 5.72529C3.97366 5.60272 3.30729 5.67307 2.70257 5.92754C2.09784 6.18201 1.58166 6.60928 1.21869 7.15582C0.855722 7.70235 0.662109 8.34385 0.662109 8.99993C0.662109 9.65602 0.855722 10.2975 1.21869 10.844C1.58166 11.3906 2.09784 11.8179 2.70257 12.0723C3.30729 12.3268 3.97366 12.3972 4.6182 12.2746C5.26273 12.152 5.85676 11.842 6.32586 11.3833L10.7259 13.4083C10.6895 13.6035 10.67 13.8014 10.6675 13.9999C10.6675 14.6592 10.863 15.3037 11.2293 15.8518C11.5956 16.4 12.1162 16.8272 12.7252 17.0795C13.3343 17.3318 14.0046 17.3978 14.6512 17.2692C15.2978 17.1406 15.8917 16.8231 16.3579 16.357C16.8241 15.8908 17.1415 15.2968 17.2701 14.6502C17.3988 14.0036 17.3328 13.3334 17.0805 12.7243C16.8282 12.1152 16.4009 11.5946 15.8528 11.2284C15.3046 10.8621 14.6601 10.6666 14.0009 10.6666ZM14.0009 2.33327C14.3305 2.33327 14.6527 2.43101 14.9268 2.61415C15.2009 2.79729 15.4145 3.05758 15.5407 3.36213C15.6668 3.66667 15.6998 4.00178 15.6355 4.32508C15.5712 4.64839 15.4125 4.94536 15.1794 5.17844C14.9463 5.41153 14.6493 5.57027 14.326 5.63458C14.0027 5.69888 13.6676 5.66588 13.3631 5.53973C13.0585 5.41359 12.7982 5.19997 12.6151 4.92588C12.4319 4.6518 12.3342 4.32957 12.3342 3.99993C12.3342 3.55791 12.5098 3.13398 12.8223 2.82142C13.1349 2.50886 13.5588 2.33327 14.0009 2.33327ZM4.00086 10.6666C3.67122 10.6666 3.34899 10.5689 3.07491 10.3857C2.80083 10.2026 2.5872 9.94228 2.46106 9.63774C2.33491 9.3332 2.30191 8.99809 2.36622 8.67478C2.43052 8.35148 2.58926 8.05451 2.82235 7.82142C3.05543 7.58834 3.35241 7.4296 3.67571 7.36529C3.99901 7.30098 4.33412 7.33399 4.63866 7.46013C4.94321 7.58628 5.2035 7.7999 5.38664 8.07398C5.56978 8.34807 5.66752 8.6703 5.66752 8.99993C5.66752 9.44196 5.49193 9.86588 5.17937 10.1784C4.86681 10.491 4.44289 10.6666 4.00086 10.6666ZM14.0009 15.6666C13.6712 15.6666 13.349 15.5689 13.0749 15.3857C12.8008 15.2026 12.5872 14.9423 12.4611 14.6377C12.3349 14.3332 12.3019 13.9981 12.3662 13.6748C12.4305 13.3515 12.5893 13.0545 12.8223 12.8214C13.0554 12.5883 13.3524 12.4296 13.6757 12.3653C13.999 12.301 14.3341 12.334 14.6387 12.4601C14.9432 12.5863 15.2035 12.7999 15.3866 13.074C15.5698 13.3481 15.6675 13.6703 15.6675 13.9999C15.6675 14.442 15.4919 14.8659 15.1794 15.1784C14.8668 15.491 14.4429 15.6666 14.0009 15.6666Z" fill="#AFAFB4" />
                                </svg>
                                Share Cite List
                              </MenuItem>
                              <MenuItem
                                className="settings-item-new"
                                onClick={(e) => {
                                  e.domEvent.stopPropagation();
                                  downloadCiteList(citeList);
                                }}
                              >
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M16.4993 10.6667C16.2783 10.6667 16.0664 10.7545 15.9101 10.9108C15.7538 11.0671 15.666 11.2791 15.666 11.5001V14.8334C15.666 15.0544 15.5782 15.2664 15.4219 15.4227C15.2657 15.579 15.0537 15.6667 14.8327 15.6667H3.16602C2.945 15.6667 2.73304 15.579 2.57676 15.4227C2.42048 15.2664 2.33268 15.0544 2.33268 14.8334V11.5001C2.33268 11.2791 2.24489 11.0671 2.0886 10.9108C1.93232 10.7545 1.72036 10.6667 1.49935 10.6667C1.27834 10.6667 1.06637 10.7545 0.910093 10.9108C0.753813 11.0671 0.666016 11.2791 0.666016 11.5001V14.8334C0.666016 15.4965 0.929408 16.1323 1.39825 16.6012C1.86709 17.07 2.50297 17.3334 3.16602 17.3334H14.8327C15.4957 17.3334 16.1316 17.07 16.6005 16.6012C17.0693 16.1323 17.3327 15.4965 17.3327 14.8334V11.5001C17.3327 11.2791 17.2449 11.0671 17.0886 10.9108C16.9323 10.7545 16.7204 10.6667 16.4993 10.6667ZM8.40768 12.0917C8.48694 12.1676 8.58039 12.2271 8.68268 12.2667C8.78243 12.3108 8.89029 12.3336 8.99935 12.3336C9.10841 12.3336 9.21627 12.3108 9.31602 12.2667C9.41831 12.2271 9.51176 12.1676 9.59102 12.0917L12.9243 8.75842C13.0813 8.6015 13.1694 8.38867 13.1694 8.16675C13.1694 7.94483 13.0813 7.732 12.9243 7.57508C12.7674 7.41816 12.5546 7.33001 12.3327 7.33001C12.1108 7.33001 11.8979 7.41816 11.741 7.57508L9.83268 9.49175V1.50008C9.83268 1.27907 9.74489 1.06711 9.5886 0.910826C9.43232 0.754545 9.22036 0.666748 8.99935 0.666748C8.77834 0.666748 8.56637 0.754545 8.41009 0.910826C8.25381 1.06711 8.16602 1.27907 8.16602 1.50008V9.49175L6.25768 7.57508C6.17998 7.49738 6.08774 7.43575 5.98622 7.3937C5.88471 7.35165 5.7759 7.33001 5.66602 7.33001C5.55613 7.33001 5.44733 7.35165 5.34581 7.3937C5.24429 7.43575 5.15205 7.49738 5.07435 7.57508C4.99665 7.65278 4.93502 7.74502 4.89297 7.84654C4.85092 7.94806 4.82927 8.05687 4.82927 8.16675C4.82927 8.27663 4.85092 8.38544 4.89297 8.48696C4.93502 8.58847 4.99665 8.68072 5.07435 8.75842L8.40768 12.0917Z" fill="#AFAFB4" />
                                </svg>
                                Download PDF
                                {citeList.cases_count !== 1 ? 's' : ''}
                              </MenuItem>
                              <MenuItem
                                className="settings-item-new"
                                onClick={(e) => {
                                  e.domEvent.stopPropagation();
                                  downloadCiteListPdf(citeList);
                                }}
                              >
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M16.4993 10.6667C16.2783 10.6667 16.0664 10.7545 15.9101 10.9108C15.7538 11.0671 15.666 11.2791 15.666 11.5001V14.8334C15.666 15.0544 15.5782 15.2664 15.4219 15.4227C15.2657 15.579 15.0537 15.6667 14.8327 15.6667H3.16602C2.945 15.6667 2.73304 15.579 2.57676 15.4227C2.42048 15.2664 2.33268 15.0544 2.33268 14.8334V11.5001C2.33268 11.2791 2.24489 11.0671 2.0886 10.9108C1.93232 10.7545 1.72036 10.6667 1.49935 10.6667C1.27834 10.6667 1.06637 10.7545 0.910093 10.9108C0.753813 11.0671 0.666016 11.2791 0.666016 11.5001V14.8334C0.666016 15.4965 0.929408 16.1323 1.39825 16.6012C1.86709 17.07 2.50297 17.3334 3.16602 17.3334H14.8327C15.4957 17.3334 16.1316 17.07 16.6005 16.6012C17.0693 16.1323 17.3327 15.4965 17.3327 14.8334V11.5001C17.3327 11.2791 17.2449 11.0671 17.0886 10.9108C16.9323 10.7545 16.7204 10.6667 16.4993 10.6667ZM8.40768 12.0917C8.48694 12.1676 8.58039 12.2271 8.68268 12.2667C8.78243 12.3108 8.89029 12.3336 8.99935 12.3336C9.10841 12.3336 9.21627 12.3108 9.31602 12.2667C9.41831 12.2271 9.51176 12.1676 9.59102 12.0917L12.9243 8.75842C13.0813 8.6015 13.1694 8.38867 13.1694 8.16675C13.1694 7.94483 13.0813 7.732 12.9243 7.57508C12.7674 7.41816 12.5546 7.33001 12.3327 7.33001C12.1108 7.33001 11.8979 7.41816 11.741 7.57508L9.83268 9.49175V1.50008C9.83268 1.27907 9.74489 1.06711 9.5886 0.910826C9.43232 0.754545 9.22036 0.666748 8.99935 0.666748C8.77834 0.666748 8.56637 0.754545 8.41009 0.910826C8.25381 1.06711 8.16602 1.27907 8.16602 1.50008V9.49175L6.25768 7.57508C6.17998 7.49738 6.08774 7.43575 5.98622 7.3937C5.88471 7.35165 5.7759 7.33001 5.66602 7.33001C5.55613 7.33001 5.44733 7.35165 5.34581 7.3937C5.24429 7.43575 5.15205 7.49738 5.07435 7.57508C4.99665 7.65278 4.93502 7.74502 4.89297 7.84654C4.85092 7.94806 4.82927 8.05687 4.82927 8.16675C4.82927 8.27663 4.85092 8.38544 4.89297 8.48696C4.93502 8.58847 4.99665 8.68072 5.07435 8.75842L8.40768 12.0917Z" fill="#AFAFB4" />
                                </svg>
                                Download Cite List
                              </MenuItem>
                              <MenuItem
                                className="settings-item-new"
                                onClick={(e) => {
                                  e.domEvent.stopPropagation();
                                  setNameEditModalId(citeList.id);
                                }}
                              >
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3.16602 14H6.69935C6.80902 14.0007 6.91774 13.9796 7.01927 13.9382C7.1208 13.8967 7.21315 13.8356 7.29102 13.7584L13.0577 7.98336L15.4243 5.6667C15.5025 5.58923 15.5645 5.49706 15.6068 5.39551C15.6491 5.29396 15.6708 5.18504 15.6708 5.07503C15.6708 4.96502 15.6491 4.8561 15.6068 4.75455C15.5645 4.653 15.5025 4.56083 15.4243 4.48336L11.891 0.908364C11.8135 0.830257 11.7214 0.768262 11.6198 0.725954C11.5183 0.683647 11.4094 0.661865 11.2993 0.661865C11.1893 0.661865 11.0804 0.683647 10.9789 0.725954C10.8773 0.768262 10.7852 0.830257 10.7077 0.908364L8.35768 3.2667L2.57435 9.0417C2.49711 9.11956 2.43601 9.21191 2.39454 9.31344C2.35307 9.41497 2.33205 9.52369 2.33268 9.63336V13.1667C2.33268 13.3877 2.42048 13.5997 2.57676 13.756C2.73304 13.9122 2.945 14 3.16602 14ZM11.2993 2.67503L13.6577 5.03336L12.4743 6.2167L10.116 3.85836L11.2993 2.67503ZM3.99935 9.97503L8.94102 5.03336L11.2993 7.3917L6.35768 12.3334H3.99935V9.97503ZM16.4993 15.6667H1.49935C1.27834 15.6667 1.06637 15.7545 0.910093 15.9108C0.753813 16.0671 0.666016 16.279 0.666016 16.5C0.666016 16.721 0.753813 16.933 0.910093 17.0893C1.06637 17.2456 1.27834 17.3334 1.49935 17.3334H16.4993C16.7204 17.3334 16.9323 17.2456 17.0886 17.0893C17.2449 16.933 17.3327 16.721 17.3327 16.5C17.3327 16.279 17.2449 16.0671 17.0886 15.9108C16.9323 15.7545 16.7204 15.6667 16.4993 15.6667Z" fill="#909096" />
                                </svg>
                                Edit Name
                              </MenuItem>
                              {citeList.search_query && (
                              <MenuItem
                                className="settings-item-new"
                                onClick={(e) => {
                                  e.domEvent.stopPropagation();
                                  convertToStatic(citeList);
                                }}
                              >
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3.16602 14H6.69935C6.80902 14.0007 6.91774 13.9796 7.01927 13.9382C7.1208 13.8967 7.21315 13.8356 7.29102 13.7584L13.0577 7.98336L15.4243 5.6667C15.5025 5.58923 15.5645 5.49706 15.6068 5.39551C15.6491 5.29396 15.6708 5.18504 15.6708 5.07503C15.6708 4.96502 15.6491 4.8561 15.6068 4.75455C15.5645 4.653 15.5025 4.56083 15.4243 4.48336L11.891 0.908364C11.8135 0.830257 11.7214 0.768262 11.6198 0.725954C11.5183 0.683647 11.4094 0.661865 11.2993 0.661865C11.1893 0.661865 11.0804 0.683647 10.9789 0.725954C10.8773 0.768262 10.7852 0.830257 10.7077 0.908364L8.35768 3.2667L2.57435 9.0417C2.49711 9.11956 2.43601 9.21191 2.39454 9.31344C2.35307 9.41497 2.33205 9.52369 2.33268 9.63336V13.1667C2.33268 13.3877 2.42048 13.5997 2.57676 13.756C2.73304 13.9122 2.945 14 3.16602 14ZM11.2993 2.67503L13.6577 5.03336L12.4743 6.2167L10.116 3.85836L11.2993 2.67503ZM3.99935 9.97503L8.94102 5.03336L11.2993 7.3917L6.35768 12.3334H3.99935V9.97503ZM16.4993 15.6667H1.49935C1.27834 15.6667 1.06637 15.7545 0.910093 15.9108C0.753813 16.0671 0.666016 16.279 0.666016 16.5C0.666016 16.721 0.753813 16.933 0.910093 17.0893C1.06637 17.2456 1.27834 17.3334 1.49935 17.3334H16.4993C16.7204 17.3334 16.9323 17.2456 17.0886 17.0893C17.2449 16.933 17.3327 16.721 17.3327 16.5C17.3327 16.279 17.2449 16.0671 17.0886 15.9108C16.9323 15.7545 16.7204 15.6667 16.4993 15.6667Z" fill="#909096" />
                                </svg>
                                Convert to Static
                              </MenuItem>
                              )}
                              <MenuItem
                                className="settings-item-new"
                                onClick={(e) => {
                                  e.domEvent.stopPropagation();
                                  setDeleteListModalId(citeList.id);
                                }}
                              >
                                <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M14.6667 4.00008H11.3333V3.16675C11.3333 2.50371 11.0699 1.86782 10.6011 1.39898C10.1323 0.93014 9.49637 0.666748 8.83333 0.666748H7.16667C6.50363 0.666748 5.86774 0.93014 5.3989 1.39898C4.93006 1.86782 4.66667 2.50371 4.66667 3.16675V4.00008H1.33333C1.11232 4.00008 0.900358 4.08788 0.744078 4.24416C0.587797 4.40044 0.5 4.6124 0.5 4.83342C0.5 5.05443 0.587797 5.26639 0.744078 5.42267C0.900358 5.57895 1.11232 5.66675 1.33333 5.66675H2.16667V14.8334C2.16667 15.4965 2.43006 16.1323 2.8989 16.6012C3.36774 17.07 4.00363 17.3334 4.66667 17.3334H11.3333C11.9964 17.3334 12.6323 17.07 13.1011 16.6012C13.5699 16.1323 13.8333 15.4965 13.8333 14.8334V5.66675H14.6667C14.8877 5.66675 15.0996 5.57895 15.2559 5.42267C15.4122 5.26639 15.5 5.05443 15.5 4.83342C15.5 4.6124 15.4122 4.40044 15.2559 4.24416C15.0996 4.08788 14.8877 4.00008 14.6667 4.00008ZM6.33333 3.16675C6.33333 2.94573 6.42113 2.73377 6.57741 2.57749C6.73369 2.42121 6.94565 2.33341 7.16667 2.33341H8.83333C9.05435 2.33341 9.26631 2.42121 9.42259 2.57749C9.57887 2.73377 9.66667 2.94573 9.66667 3.16675V4.00008H6.33333V3.16675ZM12.1667 14.8334C12.1667 15.0544 12.0789 15.2664 11.9226 15.4227C11.7663 15.579 11.5543 15.6667 11.3333 15.6667H4.66667C4.44565 15.6667 4.23369 15.579 4.07741 15.4227C3.92113 15.2664 3.83333 15.0544 3.83333 14.8334V5.66675H12.1667V14.8334Z" fill="#909096" />
                                </svg>
                                Delete Cite List
                              </MenuItem>
                            </SubMenu>
                          </Menu>
                        </div>
                      </div>
                    </div>
                    { citeList.search_query
                      && (
                      <DynamicSearchParams
                        query={citeList.search_query}
                        tags={tags}
                        jurisdictions={jurisdictions}
                      />
                      )}
                  </div>
                  { citeList.id === nameEditModalId && (
                    <NameEditModal
                      citeList={citeList}
                      fetchCiteLists={fetchCiteLists}
                      closeModal={() => setNameEditModalId(-1)}
                    />
                  )}
                  { citeList.id === pdfModalId && (
                    <PdfModal
                      citeListName={citeList.name}
                      closeModal={() => setPdfModalId(-1)}
                    />
                  )}
                  { citeList.id === deleteListModalId && (
                    <DeleteListModal
                      citeListUserId={citeList.user.id}
                      currentUserId={currentUser.id}
                      deleteCiteList={() => deleteCiteList(citeList)}
                      closeModal={() => setDeleteListModalId(-1)}
                    />
                  )}
                </>
          ))}
          { citeListType === 'shared' && sharedCiteLists.length === 0 && (
          <div className="placeholder">
            You have no cite lists shared with you.
          </div>
          )}

          { citeListType === 'my' && citeLists.length === 0 && (
          <div className="placeholder">
            You do not have any cite lists created.
          </div>
          )}
        </div>
      </div>
      <div className="right">
        { ((citeLists.length === 0 && sharedCiteLists.length === 0) || (citeLists.length > 0 && !selectedCiteList)) && (
          <div className="loader">
            <div className="name">You have not created any cite lists. To do so, create a search in case law.</div>
            <a href="/case_law">
              <div className="no-bookmarks-link">
                <div className="background-case-law-image" style={{ height: 50, width: 50 }} />
                <span>Case Law</span>
              </div>
            </a>
          </div>
        )}
        {(citeListCases !== null && citeListCases.length === 0) && (
          <div className="loader">
            <div className="name">No cases found for this cite list.</div>
          </div>
        )}
        {((Boolean(selectedCiteList)) && citeListCases) && (
          <div className="cite-list" key={selectedCiteList?.id}>
            <div className="header">
              <div className="header-title">
                { selectedCiteList?.name}
                <div className="header-count">
                  {cases_count}
                </div>
                <div className="results-sort tag">
                  Sort By:
                  <select
                    value={sortBy}
                    onChange={(e) => onSort(e.target.value)}
                  >
                    <option value="des_decision_date">Date descending</option>
                    <option value="asc_decision_date">Date ascending</option>
                  </select>
                </div>
              </div>
              <div className="header-buttons">
                <button className="bookmark-button cite-list-bookmark" type="button" onClick={() => toggleCiteListFavorite(selectedCiteList)}>
                  {favorites.cite_lists?.find((f) => f.id === selectedCiteList?.id) ? <FaBookmark /> : <FaRegBookmark />}
                </button>
                {(selectedCiteList && currentUser.id !== selectedCiteList.user?.id) && (
                  <div style={{ marginRight: '1.1rem' }}>
                    Shared By:
                    {' '}
                    {selectedCiteList.cite_list_shares.find((s) => s.user.id === currentUser.id)?.sharer.name}
                  </div>
                )}
                <SharedTo getSharedWithList={getSharedWithList} openShareDialog={() => setShareSectionOpen(true)} />
              </div>
            </div>
            {citeListCases && citeListCases.map((citeListCase) => (
              <CaseDigestPreview
                caseDigest={citeListCase.case_digest || {
                  ...citeListCase.digest,
                  published_revision: citeListCase.published_revision,
                }}
                clickedId={clickedId}
                onClick={(e) => {
                  e.stopPropagation();
                  setClickedId(citeListCase.case_digest?.id);
                }}
                onSelect={() => selectCase(citeListCase)}
                slug={citeListCase.slug}
                selectedCiteListCases={selectedCiteListCases}
                isCaseSelected={selectedCiteListCases.includes(citeListCase)}
                hasTagList
                isCiteList
                isFavorite={favorites.case_digests !== undefined && favorites.case_digests.some((digest) => digest.id === (citeListCase.case_digest?.id || citeListCase.digest?.id))}
                onToggleFavorite={() => toggleCaseBookmark(citeListCase.case_digest)}
                openVideoModal={(result) => {
                  setVideoModalVisible(true);
                  setVideoModalResult(result);
                }}
              />
            ))}

            <div className="page-buttons">
              {(page > 1) && (
                <div className="previous" onClick={() => fetchCiteListCases(page - 1)}>Previous</div>
              )}
              {(cases_count > 25
                && page < (Math.floor(cases_count / 25) + 1) && (
                  <div className="next" onClick={() => fetchCiteListCases(page + 1)}>Next</div>
              ))}
            </div>
          </div>
        )}
      </div>
      { selectedCiteListCases.length !== 0 && (
      <SelectedCasesSheet
        selectedCiteListCases={selectedCiteListCases}
        selectedCiteList={selectedCiteList}
        selectAll={selectAll}
        downloadSelectedCases={downloadSelectedCases}
        deleteSelectedCases={() => {
          if (selectedCiteList.cite_list_shares.length > 0) {
            setConfirmCiteListDeletionModalOpen(true);
          } else {
            deleteSelectedCiteListCasesFromCiteList();
          }
        }}
      />
      )}
      { loader && <SearchLoadingModal />}
      { downloadModalOpen && (
        <DownloadModal
          isDownload={downloadAction}
          plan_name={plan_name}
          closeModal={() => {
            setDownloadModalOpen(false);
            setDownloadAction(false);
          }}
          numberOfDownloads={downloadLimit}
        />
      )}
      { paywallModalOpen && (
        <PaywallModal
          isDownload={downloadAction}
          account_name={account_name}
          closeModal={() => {
            setPaywallModalOpen(false);
            setDownloadAction(false);
          }}
        />
      )}
      { shareSectionOpen && (
      <div className="side-section-container-float">
        <ShareSection
          currentUser={currentUser}
          teamMembers={teamMembers}
          getSharedWithList={getSharedWithList}
          fetchCiteLists={fetchCiteLists}
          selectedCiteList={selectedCiteList}
          closeSection={() => setShareSectionOpen(false)}
        />
      </div>
      )}
      { confirmCiteListDeletionModalOpen && (
      <ConfirmCiteListDeletionModalOpen
        confirmDelete={() => {
          setConfirmCiteListDeletionModalOpen(false);
          deleteSelectedCiteListCasesFromCiteList();
        }}
        cancelDelete={() => setConfirmCiteListDeletionModalOpen(false)}
      />
      )}
      { videoModalVisible && <VideoModal video={videoModalResult} closeModal={() => setVideoModalVisible(false)} /> }
      { citeListNotAvailableModalVisible && <DynamicCiteListNotAvailableModal closeModal={() => setCiteListNotAvailableModalVisible(false)} /> }
    </div>
  );
}

CiteLists.propTypes = {
  citeListId: PropTypes.number,
  teamMembers: PropTypes.array.isRequired,
  currentUser: PropTypes.object.isRequired,
};

CiteLists.defaultProps = {
  citeListId: null,
};
